import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { X } from 'lucide-react'
import { usePlaceRows, useNaviRegal, duplicateWarehouseLabel, downloadPdf, type NaviZelle, type NaviZaehler } from '@/api/warehouse'
import { useAuth } from '@/api/auth'
import { apiFetch } from '@/api/client'
import { warehouseErrorMessage } from '@/lib/warehouse-company'
import { getLagerForRow } from '@/lib/migration-worklist'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import RegalFront from '@/components/warehouse/RegalFront'
import { GrundartikelLink } from '@/components/catalog/GrundartikelLink'
import { NOTIZ_STREIFEN, SCHRAFFUR, STATUS_KLASSE, STATUS_TEXT } from './navi-farben'

const zellKey = (field: number, slot: number, level: number) => `${field}:${slot}:${level}`
const datum = (value: string | null) => value ? new Date(`${value}T00:00:00`).toLocaleDateString('de-DE') : '—'

/** Reihenwahl, Zähler und Frontansicht einer Reihe. `?reihe=` waehlt, `?platz=` markiert und scrollt. */
export default function RegalePage() {
  const druck = useDruckVorgaenge()
  const [params, setParams] = useSearchParams()
  const reihe = (params.get('reihe') ?? '').trim().toUpperCase()
  const platz = params.get('platz') ?? ''
  const rows = usePlaceRows()
  const regal = useNaviRegal(reihe)
  const zellen = useMemo(() => new Map((regal.data?.zellen ?? []).map(z => [zellKey(z.field_no, z.slot_no, z.level), z])), [regal.data])
  const columns = useMemo(() => [...new Map((regal.data?.zellen ?? []).map(z => [`${z.field_no}:${z.slot_no}`,
    { field: z.field_no, slot: z.slot_no, label: `F${String(z.field_no).padStart(2, '0')}·P${z.slot_no}` }])).values()]
    .sort((a, b) => a.field - b.field || a.slot - b.slot), [regal.data])
  const levels = useMemo(() => [...new Set((regal.data?.zellen ?? []).map(z => z.level))], [regal.data])
  const gewaehlt = regal.data?.zellen.find(z => z.barcode === platz)

  useEffect(() => {
    if (!platz || !regal.data) return
    const el = [...document.querySelectorAll<HTMLElement>('[data-platz]')].find(e => e.dataset['platz'] === platz)
    const ruhig = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    el?.scrollIntoView?.({ block: 'center', inline: 'center', behavior: ruhig ? 'auto' : 'smooth' })
  }, [platz, regal.data])

  function setze(werte: Record<string, string | null>) {
    setParams(prev => {
      const next = new URLSearchParams(prev)
      for (const [k, v] of Object.entries(werte)) if (v) next.set(k, v); else next.delete(k)
      return next
    }, { replace: true })
  }

  const lager = ([1, 2] as const).map(nr => ({ nr, reihen: (rows.data ?? []).filter(r => getLagerForRow(r.rowCode) === nr) }))

  return <div className="px-4 py-4 max-w-7xl mx-auto space-y-4">
    <section aria-label="Reihe wählen" className="space-y-2">
      {rows.isError && <p role="alert" className="text-sm text-red-700">{warehouseErrorMessage(rows.error)}</p>}
      {lager.map(({ nr, reihen }) => reihen.length > 0 && <div key={nr} className="flex flex-wrap items-center gap-1.5">
        <span className="w-16 shrink-0 text-[11px] uppercase tracking-wider text-muted">Lager {nr}</span>
        {reihen.map(r => <button key={r.rowCode} type="button" aria-pressed={reihe === r.rowCode}
          onClick={() => setze({ reihe: r.rowCode, platz: null })}
          className={cn('font-mono text-sm px-2 py-1 rounded border focus-visible:outline-2 focus-visible:outline-primary',
            reihe === r.rowCode ? 'bg-[#33414f] border-[#33414f] text-white' : 'bg-white border-line hover:border-[#33414f]')}>
          {r.rowCode}
        </button>)}
      </div>)}
    </section>

    {!reihe ? <p className="text-sm text-ink-2">Eine Reihe wählen, um die Belegung zu sehen.</p>
      : regal.isError ? <p role="alert" className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm">{warehouseErrorMessage(regal.error)}</p>
      : regal.isPending ? <p className="text-muted">Lade Regal {reihe}…</p>
      : <div className="flex flex-col lg:flex-row gap-4 items-start">
        <section className="min-w-0 flex-1 rounded-xl border border-line bg-panel p-3 space-y-3" aria-label={`Regal ${reihe}`}>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="font-mono text-2xl font-bold tracking-tight">{reihe}</h2>
            <Zaehler z={regal.data.zaehler} />
          </div>
          <RegalFront columns={columns} levels={levels} ariaLabel={`Regal ${reihe}`}
            cellAt={pos => zellen.get(zellKey(pos.field, pos.slot, pos.level))}
            renderCell={zelle => <NaviCell zelle={zelle} aktiv={!!zelle && zelle.barcode === platz} />}
            onCellClick={zelle => { if (zelle) setze({ platz: zelle.barcode }) }} />
        </section>
        {gewaehlt && <PlatzPanel key={gewaehlt.place_id} zelle={gewaehlt} druck={druck} onClose={() => setze({ platz: null })} />}
      </div>}
  </div>
}

export function Zaehler({ z }: { z: NaviZaehler }) {
  return <dl className="flex flex-wrap gap-x-4 gap-y-1 text-sm" aria-label="Zähler">
    <Merkmal farbe="bg-white border border-dashed border-[#b8c2cc]" label="frei" wert={z.frei} />
    <Merkmal farbe="bg-[#33414f]" label="belegt" wert={z.belegt} />
    <Merkmal farbe="bg-slate-100 border border-slate-400" label="Notiz" wert={z.notiz ?? 0} />
    {z.wartet > 0 && <Merkmal farbe="bg-warn-soft border border-[#f59e0b]" label="wartet" wert={z.wartet} />}
    {z.packplatz > 0 && <Merkmal farbe="bg-white border-l-4 border-primary" label="Packplatz" wert={z.packplatz} />}
    <div className="flex items-baseline gap-1 text-ink-2"><dt>Plätze</dt><dd className="tabular-nums">{z.plaetze}</dd></div>
  </dl>
}

function Merkmal({ farbe, label, wert }: { farbe: string; label: string; wert: number }) {
  return <div className="flex items-center gap-1.5">
    <span aria-hidden className={cn('inline-block size-3 rounded-sm', farbe)} />
    <dt>{label}</dt><dd className="font-semibold tabular-nums">{wert}</dd>
  </div>
}

function NaviCell({ zelle, aktiv }: { zelle: NaviZelle | undefined; aktiv: boolean }) {
  if (!zelle) return <div className="min-w-24 min-h-16" />
  const erste = zelle.positionen[0]
  const weitere = zelle.positionen.length + zelle.verdeckt - 1
  return <button type="button" data-platz={zelle.barcode} aria-pressed={aktiv}
    aria-label={`${zelle.barcode}: ${STATUS_TEXT[zelle.status]}${zelle.packplatz ? ', Packplatz' : ''}`}
    className={cn('relative block w-full min-w-24 min-h-16 rounded border px-1.5 py-1 text-left text-xs leading-tight transition-shadow',
      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
      STATUS_KLASSE[zelle.status], zelle.packplatz && 'border-l-4 border-l-primary',
      aktiv && 'ring-2 ring-primary ring-offset-2 ring-offset-panel')}
    style={zelle.status === 'wartet' ? { backgroundImage: SCHRAFFUR } : zelle.status === 'notiz' ? { backgroundImage: NOTIZ_STREIFEN } : undefined}>
    {zelle.status === 'belegt' ? <>
      <span className="block font-mono font-semibold truncate">{erste?.merchant_sku ?? (zelle.verdeckt ? 'fremd' : 'ohne Artikel')}</span>
      <span className="block font-mono text-[10px] opacity-80 truncate">{erste?.palette_no ?? ''}</span>
      {weitere > 0 && <span className="absolute right-1 bottom-1 rounded bg-white/20 px-1 text-[10px] font-semibold">+{weitere}</span>}
    </> : zelle.status === 'notiz' ? <span className="block font-semibold truncate">{zelle.notiz?.text}</span>
      : <span className="block font-semibold">{zelle.status === 'frei' ? 'frei' : 'wartet'}</span>}
    {zelle.packplatz && <span className={cn('block text-[10px] font-semibold mt-0.5', zelle.status === 'belegt' ? 'text-sky-200' : 'text-primary')}>Packplatz</span>}
  </button>
}

function PlatzPanel({ zelle, onClose, druck }: { zelle: NaviZelle; onClose: () => void; druck: ReturnType<typeof useDruckVorgaenge> }) {
  const { warehouse } = useAuth()
  return <aside aria-label={`Platz ${zelle.barcode}`} className="w-full lg:w-80 shrink-0 lg:sticky lg:top-4 rounded-xl border border-line bg-white p-4 space-y-3 shadow-sm">
    <div className="flex items-start justify-between gap-2">
      <div>
        <p className="text-[11px] uppercase tracking-wider text-muted">Platz</p>
        <h3 className="font-mono text-lg font-bold break-all">{zelle.barcode}</h3>
        <p className="text-sm text-ink-2">{STATUS_TEXT[zelle.status]} · {zelle.zone === 'fuss' ? 'zu Fuß' : 'Stapler'}{zelle.packplatz ? ' · Packplatz' : ''}</p>
      </div>
      <Button size="icon" variant="ghost" aria-label="Panel schließen" onClick={onClose}><X className="size-4" /></Button>
    </div>
    {zelle.positionen.length === 0 && zelle.verdeckt === 0 && <p className="text-sm text-ink-2">
      {zelle.status === 'wartet' ? 'Keine Palette im System; eine Erstaufnahme ist für diesen Platz offen.' : 'Keine Palette auf diesem Platz.'}
    </p>}
    <ul className="space-y-2">
      {zelle.positionen.map((p, i) => <li key={p.palette_no ?? i} className="rounded-lg border border-line p-3 space-y-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="font-mono font-semibold">{p.palette_no ?? 'ohne P-Nr.'}</span>
          <span className="text-sm tabular-nums">{p.quantity === null ? 'Menge unbekannt' : `${p.circa ? 'ca. ' : ''}${p.quantity} ${p.unit}`}</span>
        </div>
        {p.merchant_sku && <GrundartikelLink code={p.merchant_sku} className="font-mono font-semibold" />}
        {p.name && <p className="text-sm text-ink-2">{p.name}</p>}
        <p className="text-xs text-muted">First-In {datum(p.first_in)}</p>
        {warehouse.lead && p.label_id != null && p.palette_no && <EtikettNachdruck labelId={p.label_id} druck={druck} />}
      </li>)}
    </ul>
    {zelle.verdeckt > 0 && <p className="text-sm text-ink-2">{zelle.verdeckt} {zelle.verdeckt === 1 ? 'Palette' : 'Paletten'} einer anderen Firma, ohne Details.</p>}
    {(zelle.status === 'frei' || zelle.status === 'notiz') && <PlatzNotiz zelle={zelle} leiter={warehouse.lead} />}
  </aside>
}

function PlatzNotiz({ zelle, leiter }: { zelle: NaviZelle; leiter: boolean }) {
  const qc = useQueryClient()
  const [text, setText] = useState(zelle.notiz?.text ?? '')
  const [editing, setEditing] = useState(!zelle.notiz)
  const [bestaetigen, setBestaetigen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const versuch = useRef<{ payload: string; key: string } | null>(null)
  async function speichern(release: boolean) {
    if (busy) return
    const payload = JSON.stringify({ place_id: zelle.place_id, ...(release ? {} : { text: text.trim() }) })
    if (versuch.current?.payload !== payload) versuch.current = { payload, key: crypto.randomUUID() }
    setBusy(true); setError(null)
    try {
      await apiFetch(`/warehouse/navi/platz-notiz/${release ? 'freigeben/' : ''}`, { method: 'POST',
        body: JSON.stringify({ ...JSON.parse(payload), idempotency_key: versuch.current.key }) })
      versuch.current = null; setBestaetigen(false); setEditing(release)
      if (release) setText('')
      await qc.invalidateQueries({ queryKey: ['warehouse', 'navi'] })
      void qc.invalidateQueries({ queryKey: ['warehouse', 'leerplatz'] })
    } catch (e) { setError(e) } finally { setBusy(false) }
  }
  return <div className="space-y-2 border-t border-line pt-3">
    {zelle.notiz && <div className="text-sm"><p className="font-semibold break-words">{zelle.notiz.text}</p>
      <p className="text-xs text-ink-2"><span>{zelle.notiz.von}</span> · {new Date(zelle.notiz.seit).toLocaleString('de-DE')}</p></div>}
    {leiter && editing && <div className="space-y-2"><label className="block text-sm">Was steht hier? (keine Lagerware)
      <Input className="mt-1" value={text} maxLength={120} onChange={e => setText(e.target.value)} /></label>
      <Button size="sm" disabled={busy || !text.trim()} onClick={() => void speichern(false)}>Speichern</Button></div>}
    {leiter && zelle.notiz && !editing && <div className="flex flex-wrap gap-2">
      <Button size="sm" variant="outline" disabled={busy} onClick={() => { setText(zelle.notiz!.text); setEditing(true) }}>Ändern</Button>
      <Button size="sm" variant="outline" disabled={busy} onClick={() => setBestaetigen(true)}>Notiz löschen</Button>
    </div>}
    {leiter && bestaetigen && <div className="space-y-2 text-sm"><p>Notiz löschen und den Platz freigeben?</p>
      <div className="flex gap-2"><Button size="sm" disabled={busy} onClick={() => void speichern(true)}>Löschen bestätigen</Button>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => setBestaetigen(false)}>Abbrechen</Button></div></div>}
    {error != null && <p role="alert" className="text-sm text-red-700">{warehouseErrorMessage(error)}</p>}
  </div>
}

type DruckArt = 'front' | 'seite'
type DruckVorgang = { key: string; phase: 'zaehlen' | 'pdf'; rest: DruckArt[]; pending: boolean; fehler: unknown }
let druckVorgaenge: Record<number, DruckVorgang> = {}
const druckAbonnenten = new Set<() => void>()
function abonnierenDruck(listener: () => void) {
  druckAbonnenten.add(listener)
  return () => { druckAbonnenten.delete(listener) }
}
function druckSnapshot() { return druckVorgaenge }
function speichernDruck(labelId: number, vorgang: DruckVorgang | null) {
  const next = { ...druckVorgaenge }
  if (vorgang) next[labelId] = vorgang
  else delete next[labelId]
  druckVorgaenge = next
  druckAbonnenten.forEach(listener => listener())
}
/** Nur fuer isolierte Tests; laufende Vorgaenge duerfen den Reset nicht ueberschreiben. */
export function resetDruckVorgaengeForTests() {
  druckVorgaenge = {}
  druckAbonnenten.forEach(listener => listener())
}
function useDruckVorgaenge() {
  const qc = useQueryClient()
  const vorgaenge = useSyncExternalStore(abonnierenDruck, druckSnapshot, druckSnapshot)

  async function drucken(labelId: number, auswahl: DruckArt | 'beide') {
    let v = druckVorgaenge[labelId]
    if (v?.pending) return
    v = v ? { ...v, pending: true, fehler: null } : { key: crypto.randomUUID(), phase: 'zaehlen', rest: auswahl === 'beide' ? ['front', 'seite'] : [auswahl], pending: true, fehler: null }
    speichernDruck(labelId, v)
    try {
      if (v.phase === 'zaehlen') {
        await duplicateWarehouseLabel(labelId, v.key)
        if (druckVorgaenge[labelId]?.key !== v.key) return
        v = { ...v, phase: 'pdf' }
        speichernDruck(labelId, v)
        void qc.invalidateQueries({ queryKey: ['warehouse', 'navi'] })
      }
      while (v.rest.length) {
        const art = v.rest[0]!
        await downloadPdf(`/warehouse/label/${labelId}/nachdruck-pdf/`, `duplikat-${labelId}-${art}.pdf`, { art })
        if (druckVorgaenge[labelId]?.key !== v.key) return
        v = { ...v, rest: v.rest.slice(1) }
        speichernDruck(labelId, v)
      }
      speichernDruck(labelId, null)
    } catch (error) {
      if (druckVorgaenge[labelId]?.key === v.key) speichernDruck(labelId, { ...v, pending: false, fehler: error })
    }
  }
  return { vorgaenge, drucken }
}

function EtikettNachdruck({ labelId, druck }: { labelId: number; druck: ReturnType<typeof useDruckVorgaenge> }) {
  const [offen, setOffen] = useState(false)
  const [auswahl, setAuswahl] = useState<DruckArt | 'beide'>('front')
  const v = druck.vorgaenge[labelId]
  function ausfuehren() { setOffen(false); void druck.drucken(labelId, auswahl) }

  return <div className="pt-2 space-y-2">
    <Button type="button" variant="outline" size="sm" disabled={!!v}
      onClick={() => setOffen(true)}>Etikett nachdrucken</Button>
    {(offen || v) && <div className="space-y-2 text-sm text-ink-2">
      <p>{v?.phase === 'pdf' ? 'Der Zähler wurde erhöht. Ausstehendes PDF erneut holen.' : 'Wird als DUPLIKAT gedruckt, der Zähler steigt um 1'}</p>
      {!v && <fieldset className="space-y-1">
        <legend className="text-xs text-muted">Etikett auswählen</legend>
        {([['front', 'Front (A4)'], ['seite', 'Seite (2 Seiten-Etiketten)'], ['beide', 'Beide']] as const).map(([wert, text]) =>
          <label key={wert} className="flex items-center gap-2"><input type="radio" name={`nachdruck-${labelId}`}
            value={wert} checked={auswahl === wert} onChange={() => setAuswahl(wert)} />{text}</label>)}
      </fieldset>}
      {v?.fehler != null && <p role="alert" className="text-sm text-red-700">{warehouseErrorMessage(v.fehler)}</p>}
      {v?.pending ? <Button type="button" size="sm" disabled>Wird geladen…</Button> : v
        ? <Button type="button" size="sm" onClick={ausfuehren}>{v.phase === 'pdf' ? 'PDF erneut holen' : 'Erneut versuchen'}</Button>
        : <div className="flex gap-2"><Button type="button" size="sm" onClick={ausfuehren}>Drucken</Button>
          <Button type="button" variant="outline" size="sm" onClick={() => setOffen(false)}>Abbrechen</Button></div>}
    </div>}
  </div>
}
